import { useQuery } from "@tanstack/react-query";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Grid,
  Paper,
  Typography,
} from "@mui/material";
import {
  Store as VendorIcon,
  Verified as ActiveIcon,
  Apartment as ApartmentIcon,
  ArrowBack as ArrowBackIcon,
} from "@mui/icons-material";
import { useNavigate, useSearchParams } from "react-router-dom";
import vendorProfileService from "@/services/vendorProfileService";
import type { VMVendorSummary } from "@/services/vendorProfileService";
import { StatusBadge } from "./components/StatusBadge";
import { IdentityAndTaxSection } from "./sections/IdentityAndTaxSection";
import { AddressSection } from "./sections/AddressSection";
import { ContactNotificationSection } from "./sections/ContactNotificationSection";
import { BankingSection } from "./sections/BankingSection";
import { CommercialTermsSection } from "./sections/CommercialTermsSection";
import { VendorDocumentsSection } from "./sections/VendorDocumentsSection";
import { VMQuestionnaireSection } from "./sections/VMQuestionnaireSection";
import { VendorProfileTypeSection } from "./sections/VendorProfileTypeSection";
import { useVendorCategories } from "@/pages/VendorOnboarding/hooks/useVendorCategories";
import { isKwsInstallation } from "./utils/vendorCategories";
import { BranchIdentitySection } from "./sections/BranchIdentitySection";
import { PortalAccessSection } from "./sections/PortalAccessSection";
import type { FacilityViewSlice, VendorType } from "./types";
import { useIsInternalReviewer } from "./utils/useIsInternalReviewer";
import { VMVendorPicker } from "./VMVendorPicker";
import { PendingReviewsCard } from "./PendingReviewsCard";
import { PostOnboardingGovernance } from "./edit/PostOnboardingGovernance";

const SUCCESS_BG = "#E6F4EA";
const SUCCESS_BORDER = "#B7E4C7";
const SUCCESS_DARK = "#0F5132";

function chooseSelfVendor(
  vendors: VMVendorSummary[],
  requested: string,
): VMVendorSummary | undefined {
  const asked = vendors.find((v) => v.vendor_id === requested);
  if (asked) return asked;
  return vendors.length === 1 ? vendors[0] : undefined;
}

function BranchCard({
  facility,
  vendorType,
  showPrimaryChip,
}: {
  facility: FacilityViewSlice;
  vendorType: VendorType;
  showPrimaryChip: boolean;
}) {
  const { profile, documents, contact_emails } = facility;
  const isKws = isKwsInstallation(profile);
  return (
    <Paper
      variant="outlined"
      sx={{
        borderColor: "#E0E4EF",
        borderRadius: "12px",
        overflow: "hidden",
        mt: 3,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          gap: 2,
          px: 4,
          py: 2.5,
          borderBottom: 1,
          borderColor: "#E0E4EF",
          bgcolor: "#FAFBFF",
        }}
      >
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: 1,
            bgcolor: "primary.light",
            color: "primary.main",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <ApartmentIcon sx={{ fontSize: 20 }} />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontSize: 16, fontWeight: 700 }}>
            {profile.display_name}
            {showPrimaryChip && (
              <Typography
                component="span"
                sx={{
                  ml: 1,
                  fontSize: 10,
                  fontWeight: 700,
                  bgcolor: "#EBEEFE",
                  color: "#2B44C1",
                  px: 1,
                  py: 0.25,
                  borderRadius: 0.5,
                  letterSpacing: 0.5,
                }}
              >
                PRIMARY
              </Typography>
            )}
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
            {[profile.gst, profile.billing_city].filter(Boolean).join(" · ")}
          </Typography>
        </Box>
        <StatusBadge status={profile.status} />
      </Box>

      <Box sx={{ px: 3, py: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <BranchIdentitySection
              profile={profile}
              documents={documents}
              vendorType={vendorType}
            />
            <BankingSection
              profile={profile}
              vendorType={vendorType}
              documents={documents}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <AddressSection profile={profile} />
            <ContactNotificationSection
              profile={profile}
              contactEmails={contact_emails}
            />
            <PortalAccessSection contactEmails={contact_emails} isKws={isKws} />
          </Grid>
        </Grid>
      </Box>
    </Paper>
  );
}

export function VendorProfilePage() {
  const isInternal = useIsInternalReviewer();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const vendorIdParam = searchParams.get("vendor_id") ?? "";

  // "Pending with you" (ENG-27734): the reviewer's open tasks, shown under the
  // onboarded-vendors list. Internal view only — the query never runs for a vendor.
  const pendingQuery = useQuery({
    queryKey: ["vm-pending-reviews"],
    queryFn: () => vendorProfileService.getMyPendingReviews(),
    enabled: isInternal && !vendorIdParam,
    staleTime: 60 * 1000,
  });

  const pickerQuery = useQuery({
    queryKey: ["vm-reviewed-vendors"],
    queryFn: () => vendorProfileService.getVMReviewedVendors(),
    enabled: isInternal && !vendorIdParam,
    staleTime: 5 * 60 * 1000,
  });

  const categoriesQuery = useVendorCategories();
  const categories = categoriesQuery.data;

  const myVendorsQuery = useQuery({
    queryKey: ["my-vendors"],
    queryFn: () => vendorProfileService.getMyVendors("bouncer"),
    enabled: !isInternal,
    staleTime: 5 * 60 * 1000,
  });
  const myVendors = myVendorsQuery.data ?? [];
  const selfVendor = chooseSelfVendor(myVendors, vendorIdParam);
  const selfVendorId = selfVendor?.vendor_id ?? "";

  const profileQuery = useQuery({
    queryKey: ["vendor-profile", isInternal ? vendorIdParam : `self:${selfVendorId}`],
    queryFn: () =>
      isInternal
        ? vendorProfileService.getVMVendorProfile(vendorIdParam)
        : vendorProfileService.getVendorProfileByFacilityIds(
            selfVendor?.facility_ids ?? [],
            "bouncer",
          ),
    enabled: isInternal ? Boolean(vendorIdParam) : Boolean(selfVendorId),
    staleTime: 5 * 60 * 1000,
  });

  if (isInternal && !vendorIdParam) {
    if (pickerQuery.isLoading) {
      return (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
          <CircularProgress />
        </Box>
      );
    }
    if (pickerQuery.isError || !pickerQuery.data) {
      return (
        <Box sx={{ maxWidth: 720, mx: "auto", mt: 8, p: 3 }}>
          <Alert severity="error">
            Couldn&apos;t load your reviewed vendors. Please refresh to retry.
          </Alert>
        </Box>
      );
    }
    return (
      <VMVendorPicker
        vendors={pickerQuery.data}
        onSelect={(vendorId) => setSearchParams({ vendor_id: vendorId })}
        below={
          <PendingReviewsCard
            items={pendingQuery.data}
            isLoading={pendingQuery.isLoading}
            isError={pendingQuery.isError}
            onOpen={(taskId) =>
              navigate(
                `/vendor-onboarding/review?task_id=${encodeURIComponent(taskId)}`,
              )
            }
          />
        }
      />
    );
  }

  if (!isInternal && !selfVendorId) {
    if (myVendorsQuery.isLoading) {
      return (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
          <CircularProgress />
        </Box>
      );
    }
    if (myVendorsQuery.isError) {
      return (
        <Box sx={{ maxWidth: 720, mx: "auto", mt: 8, p: 3 }}>
          <Alert severity="error">
            Couldn&apos;t load your vendor profile. Please refresh to retry.
          </Alert>
        </Box>
      );
    }
    if (myVendors.length === 0) {
      return (
        <Box sx={{ maxWidth: 720, mx: "auto", mt: 8, p: 3 }}>
          <Alert severity="warning">
            No vendor account is linked to this login yet.
          </Alert>
        </Box>
      );
    }
    return (
      <VMVendorPicker
        vendors={myVendors}
        onSelect={(vendorId) => setSearchParams({ vendor_id: vendorId })}
        title="Your vendors"
        subtitle="Your login is linked to more than one vendor. Select one to view its profile."
      />
    );
  }

  const query = profileQuery;

  if (query.isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (query.isError || !query.data) {
    return (
      <Box sx={{ maxWidth: 720, mx: "auto", mt: 8, p: 3 }}>
        <Alert severity="error">
          Couldn&apos;t load{isInternal ? " the vendor" : " your vendor"} profile.
          Please refresh to retry.
        </Alert>
      </Box>
    );
  }

  const { vendor, facilities, field_governance } = query.data;
  const vendorType: VendorType = vendor?.type ?? "GST";
  const primary =
    facilities.find((f) => f.profile.is_primary) ?? facilities[0];

  if (!primary) {
    return (
      <Box sx={{ maxWidth: 720, mx: "auto", mt: 8, p: 3 }}>
        <Alert severity="warning">
          Your vendor account has no facility on file yet. Contact your Vendor
          Manager.
        </Alert>
      </Box>
    );
  }

  const legalName = vendor?.display_name ?? primary.profile.legal_name;
  const primaryFacilityId = primary.profile.id;

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#F5F7FC" }}>
      <Box sx={{ maxWidth: 1440, mx: "auto", px: 3, py: 3 }}>
        {(isInternal ? Boolean(vendorIdParam) : myVendors.length > 1) && (
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => setSearchParams({})}
            sx={{ mb: 2, textTransform: "none", color: "text.secondary" }}
          >
            {isInternal ? "Back to reviewed vendors" : "Switch vendor"}
          </Button>
        )}
        <Paper
          variant="outlined"
          sx={{
            borderColor: "#E0E4EF",
            borderRadius: "12px",
            overflow: "hidden",
            boxShadow:
              "0 1px 3px rgba(0,0,0,0.05), 0 4px 16px rgba(0,0,0,0.06)",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: 2,
              px: 4,
              py: 3,
              borderBottom: 1,
              borderColor: "#E0E4EF",
            }}
          >
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: 1,
                bgcolor: "primary.light",
                color: "primary.main",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <VendorIcon sx={{ fontSize: 22 }} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontSize: 20, fontWeight: 700 }}>
                {legalName}
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "text.secondary", mt: 0.5 }}
              >
                Vendor Profile · VIP · {legalName}
              </Typography>
            </Box>
          </Box>

          {vendor?.status === "Active" && (
            <Box sx={{ p: 3 }}>
              <Paper
                variant="outlined"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  p: 2.5,
                  borderColor: SUCCESS_BORDER,
                  bgcolor: SUCCESS_BG,
                }}
              >
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 1,
                    bgcolor: SUCCESS_DARK,
                    color: "#fff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <ActiveIcon sx={{ fontSize: 22 }} />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    sx={{ fontSize: 15, fontWeight: 700, color: SUCCESS_DARK }}
                  >
                    Vendor active in VIP
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: SUCCESS_DARK, mt: 0.25 }}
                  >
                    Profile live across VMS, VIP, ERP
                  </Typography>
                </Box>
                <StatusBadge status={vendor.status} />
              </Paper>
            </Box>
          )}

          <Box sx={{ px: 3, pb: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <IdentityAndTaxSection
                  vendor={vendor}
                  primaryFacilityVolume={primary.profile.expected_business_volume}
                  primaryFacilityState={
                    primary.profile.billing_state ??
                    primary.profile.state_of_registration
                  }
                  primaryFacilityGst={primary.profile.gst}
                  documents={primary.documents}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <VendorProfileTypeSection
                  profile={primary.profile}
                  categories={categories}
                />
                <CommercialTermsSection
                  profile={primary.profile}
                  vendorType={vendorType}
                />
                {isInternal && (
                  <VMQuestionnaireSection
                    profile={primary.profile}
                    vendor={vendor}
                  />
                )}
                {isInternal && (
                  <VendorDocumentsSection
                    vendor={vendor}
                    primary={primary.profile}
                    documents={primary.documents}
                  />
                )}
              </Grid>
            </Grid>
          </Box>
        </Paper>

        {facilities.map((f) => (
          <BranchCard
            key={f.profile.id}
            facility={f}
            vendorType={vendorType}
            showPrimaryChip={f.profile.id === primaryFacilityId}
          />
        ))}

        <PostOnboardingGovernance
          facilities={facilities}
          primaryFacilityId={primary.profile.id}
          vendor={vendor}
          governance={field_governance}
          isInternal={isInternal}
          categories={categories}
        />
      </Box>
    </Box>
  );
}

export default VendorProfilePage;
